import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import { hasAuthParams, useAuth } from "react-oidc-context";
import { Navigate, useLocation } from "react-router";
import * as Sentry from "@sentry/react";
import { deducedError } from "@/Utils/ErrorUtils";
import { useCallback, useEffect } from "react";
import { getAcrValues, scopes } from "@/Utils/EnvUtils";
import { Trans, useLingui } from "@lingui/react/macro";
import { Link } from "@medoma/components/Link/Link";
import styles from "./IdsLogin.module.scss";

const LoginLayout = ({ children }: { children: React.ReactNode }) => {
  return <div className={styles.loginLayout}>{children}</div>;
};

export const Login = () => {
  const auth = useAuth();
  const { activeNavigator, error, isAuthenticated, isLoading, user } = auth;

  const { state }: { state?: { from?: Location } } = useLocation();
  const { t } = useLingui();
  const redirectURL =
    // If there is a state in the user object, it means that the user was redirected to the login page, and this is where they came from. Respect that firstly.
    // If there is a from in the state object, it means that the user was redirected internally, and this is where they came from. Respect that secondly.
    // Default to root.
    user?.state
      ? user?.state
      : state?.from
        ? `${state?.from?.pathname}${state?.from?.search}`
        : "/";

  const signin = useCallback(() => {
    void auth.signinRedirect({
      acr_values: getAcrValues(),
      scope: scopes,
      state: redirectURL,
    });
  }, [auth, redirectURL]);

  // automatically sign-in
  useEffect(() => {
    if (
      !hasAuthParams() &&
      !isAuthenticated &&
      !activeNavigator &&
      !isLoading
    ) {
      signin();
    }
  }, [isAuthenticated, activeNavigator, isLoading, signin]);

  if (isAuthenticated) {
    return <Navigate to={redirectURL} replace />;
  }

  if (activeNavigator || isLoading) {
    return (
      <LoginLayout>
        <Loading message={t`Loggar in`} />
      </LoginLayout>
    );
  }

  if (error) {
    Sentry.captureException(error);

    if (hasAuthParams()) {
      Sentry.captureException(
        "User ended up in an error state with preserved auth params",
      );
    }

    return (
      <LoginLayout>
        <ErrorMessage
          message={`${t`Det gick inte att logga in.`} ${deducedError(error)}`}
        />
        {hasAuthParams() ? (
          <Link to="/">
            <Trans>Gå till startsidan</Trans>
          </Link>
        ) : null}
      </LoginLayout>
    );
  }

  return null;
};
